import React, { useRef, useState, useEffect } from 'react';
import { Box, Button, Typography, Stack, Grow, Paper } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CodeIcon from '@mui/icons-material/Code';
import { useTranslation } from 'react-i18next';
import {
  MediaRecorderState,
  ProgressModal,
  FeedbackSnackbar,
  stopMediaRecording,
} from './common';
import { config } from '../../../config';

interface AppSpec {
  spec: any;
  description: string;
}

export const VoiceToApp: React.FC = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<MediaRecorderState>({
    isRecording: false,
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
  });
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState('00:00');
  const [appSpec, setAppSpec] = useState<AppSpec | null>(null);

  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const audioContext = useRef<AudioContext | null>(null);
  const analyser = useRef<AnalyserNode | null>(null);
  const visualizerRef = useRef<HTMLCanvasElement>(null);
  const animationFrame = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const timerInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const pausedTimeRef = useRef<number>(0);
  const recordedBlob = useRef<Blob | null>(null);
  const audioStream = useRef<MediaStream | null>(null);

  const updateRecordingTime = () => {
    const elapsedTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const minutes = Math.floor(elapsedTime / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (elapsedTime % 60).toString().padStart(2, '0');
    setRecordingTime(`${minutes}:${seconds}`);
  };

  const drawVisualizer = () => {
    if (!visualizerRef.current || !analyser.current) return;

    const canvasCtx = visualizerRef.current.getContext('2d');
    if (!canvasCtx) return;

    const { width, height } = visualizerRef.current;
    const dataArray = new Uint8Array(analyser.current.frequencyBinCount);

    const draw = () => {
      if (!analyser.current) return;

      analyser.current.getByteFrequencyData(dataArray);
      canvasCtx.clearRect(0, 0, width, height);

      const barWidth = Math.ceil(
        (width / analyser.current.frequencyBinCount) * 2.5
      );
      const centerY = height / 2;
      let x = 0;

      canvasCtx.fillStyle = '#d1d5db';
      for (let i = 0; i < analyser.current.frequencyBinCount; i += 1) {
        const barHeight = Math.min(dataArray[i] * 1.5, height);
        canvasCtx.fillRect(
          Math.floor(x),
          Math.floor(centerY - barHeight / 2),
          Math.floor(barWidth),
          Math.floor(barHeight)
        );
        x += barWidth + 1;
      }

      animationFrame.current = requestAnimationFrame(draw);
    };

    draw();
  };

  const generateAppSpec = async (audioBlob: Blob) => {
    try {
      // Convert audio blob to base64
      const base64Audio = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result) {
            const base64 = (reader.result as string).split(',')[1];
            resolve(base64);
          } else {
            reject(new Error('Failed to convert audio to base64'));
          }
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(audioBlob);
      });

      // Send to Gemini API for analysis
      const response = await fetch(config.geminiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gemini-1.5-pro',
          contents: [
            {
              parts: [
                {
                  text: `You are an expert app architect. Listen to this voice recording where someone describes an app they want to build. 
Create a detailed but concise JSON specification for the app that includes:
1. appName: A catchy name for the app
2. description: A brief description of what the app does
3. keyFeatures: Array of main features
4. techStack: Object containing frontend, backend, database, and other technologies
5. uiComponents: Array of main UI components/screens
6. dataModels: Array of key data models with their fields
7. apiEndpoints: Array of main API endpoints

Format your response exactly like this, with no additional text:
App Description: [1-2 sentences describing the app]
JSON Spec: {
  "appName": "string",
  "description": "string",
  "keyFeatures": ["string"],
  ...rest of the JSON
}

Keep the JSON structure clean and focused on the essential details. If you can't understand the audio clearly, focus on what you can understand and make reasonable assumptions.`,
                },
                {
                  inlineData: {
                    mimeType: 'audio/webm',
                    data: base64Audio,
                  },
                },
              ],
            },
          ],
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Gemini API error:', errorText);
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.candidates?.[0]?.content?.parts?.[0]?.text) {
        console.error('Unexpected API response:', data);
        throw new Error('Invalid API response format');
      }

      const result = data.candidates[0].content.parts[0].text;

      // Parse the response
      const [description, jsonStr] = result
        .split('JSON Spec:')
        .map((s: string) => s.trim());
      if (!description || !jsonStr) {
        throw new Error('Failed to parse API response format');
      }

      try {
        const spec = JSON.parse(jsonStr);
        setAppSpec({
          description: description.replace('App Description:', '').trim(),
          spec,
        });
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new Error('Failed to parse generated JSON specification');
      }
    } catch (error) {
      console.error('Error in generateAppSpec:', error);
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to generate app specification. Please try again.',
      }));
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStream.current = stream;

      audioContext.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      analyser.current = audioContext.current.createAnalyser();
      const source = audioContext.current.createMediaStreamSource(stream);
      source.connect(analyser.current);

      analyser.current.fftSize = 2048;
      analyser.current.minDecibels = -90;
      analyser.current.maxDecibels = -10;
      analyser.current.smoothingTimeConstant = 0.85;

      const options = {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000,
      };

      if (MediaRecorder.isTypeSupported(options.mimeType)) {
        mediaRecorder.current = new MediaRecorder(stream, options);
      } else {
        mediaRecorder.current = new MediaRecorder(stream);
      }

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.current.push(event.data);
        }
      };

      mediaRecorder.current.onstop = async () => {
        const audioBlob = new Blob(chunks.current, {
          type: mediaRecorder.current?.mimeType || 'audio/webm;codecs=opus',
        });

        recordedBlob.current = audioBlob;
        setState((prev) => ({ ...prev, uploading: true }));

        await generateAppSpec(audioBlob);

        setState((prev) => ({
          ...prev,
          isRecording: false,
          uploading: false,
          uploadComplete: true,
        }));

        if (timerInterval.current) clearInterval(timerInterval.current);
        if (visualizerRef.current) visualizerRef.current.style.display = 'none';
        chunks.current = [];
      };

      mediaRecorder.current.start(1000);
      setState((prev) => ({ ...prev, isRecording: true }));
      startTimeRef.current = Date.now();
      updateRecordingTime();
      timerInterval.current = setInterval(updateRecordingTime, 1000);

      if (visualizerRef.current) {
        visualizerRef.current.style.display = 'block';
        drawVisualizer();
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: 'Error accessing microphone. Please check permissions.',
      }));
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && state.isRecording) {
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
        timerInterval.current = null;
      }
      stopMediaRecording(mediaRecorder, [audioStream.current], animationFrame);
    }
  };

  const togglePause = () => {
    if (!mediaRecorder.current) return;

    if (isPaused) {
      mediaRecorder.current.resume();
      startTimeRef.current = Date.now() - pausedTimeRef.current;
      timerInterval.current = setInterval(updateRecordingTime, 1000);
      drawVisualizer();
    } else {
      mediaRecorder.current.pause();
      pausedTimeRef.current = Date.now() - startTimeRef.current;
      if (timerInterval.current) clearInterval(timerInterval.current);
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    }
    setIsPaused(!isPaused);
  };

  const handleReset = () => {
    setState({
      isRecording: false,
      uploading: false,
      progress: 0,
      uploadComplete: false,
      error: null,
    });
    setIsPaused(false);
    setRecordingTime('00:00');
    setAppSpec(null);

    recordedBlob.current = null;
    chunks.current = [];
    startTimeRef.current = 0;
    pausedTimeRef.current = 0;

    stopMediaRecording(mediaRecorder, [audioStream.current], animationFrame);
    audioStream.current = null;

    if (timerInterval.current) {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }

    if (audioContext.current?.state !== 'closed') {
      audioContext.current?.close();
    }
    audioContext.current = null;
    analyser.current = null;

    if (visualizerRef.current) {
      const ctx = visualizerRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(
          0,
          0,
          visualizerRef.current.width,
          visualizerRef.current.height
        );
      }
      visualizerRef.current.style.display = 'none';
    }
  };

  useEffect(() => {
    return () => {
      if (timerInterval.current) clearInterval(timerInterval.current);
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    };
  }, []);

  return (
    <Box sx={{ textAlign: 'center', p: 3, maxWidth: 800, mx: 'auto' }}>
      {state.error?.includes('microphone') && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: 'error.lighter',
            border: '1px solid',
            borderColor: 'error.light',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="error">
            {t('voiceToApp.messages.microphoneAccess')}
          </Typography>
        </Paper>
      )}

      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        sx={{ mb: 4, minHeight: 56 }}
      >
        {!state.isRecording && !state.uploading && !appSpec && (
          <Button
            variant="contained"
            color="error"
            onClick={startRecording}
            startIcon={<MicIcon />}
            size="large"
            sx={{
              minWidth: 200,
              px: 4,
              borderRadius: 28,
              py: 2,
              fontSize: '1.1rem',
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
              '&:hover': {
                backgroundColor: 'error.dark',
                '& .MuiSvgIcon-root': {
                  color: 'white',
                },
              },
            }}
          >
            {t('voiceToApp.controls.start')}
          </Button>
        )}
        {state.isRecording && (
          <>
            <Grow in={state.isRecording} timeout={300}>
              <Button
                variant="contained"
                color="error"
                onClick={stopRecording}
                startIcon={<CodeIcon />}
                size="large"
                sx={{
                  minWidth: 180,
                  px: 3,
                  borderRadius: 28,
                  py: 2,
                  fontSize: '1.1rem',
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  },
                  '&:hover': {
                    backgroundColor: 'error.dark',
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                  },
                }}
              >
                {t('voiceToApp.controls.stop')} ({recordingTime})
              </Button>
            </Grow>
            <Grow in={state.isRecording} timeout={300}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={togglePause}
                startIcon={isPaused ? <PlayArrowIcon /> : <PauseIcon />}
                size="large"
                sx={{
                  minWidth: 140,
                  px: 3,
                  borderRadius: 28,
                  py: 2,
                  fontSize: '1.1rem',
                }}
              >
                {isPaused
                  ? t('voiceToApp.controls.resume')
                  : t('voiceToApp.controls.pause')}
              </Button>
            </Grow>
          </>
        )}
      </Stack>

      <Box
        component="canvas"
        ref={visualizerRef}
        sx={{
          width: '100%',
          height: 150,
          my: 3,
          display: 'none',
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      />

      {appSpec && (
        <Grow in={!!appSpec} timeout={500}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mt: 4,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              maxWidth: 600,
              mx: 'auto',
              backgroundColor: 'background.paper',
            }}
          >
            <Stack spacing={3} alignItems="center">
              <Typography variant="h6" color="primary" gutterBottom>
                {t('voiceToApp.results.appDescription')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.primary',
                  textAlign: 'left',
                  lineHeight: 1.6,
                  mb: 2,
                }}
              >
                {appSpec.description}
              </Typography>

              <Typography variant="h6" color="primary" gutterBottom>
                {t('voiceToApp.results.technicalSpec')}
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: 'grey.900',
                  p: 2,
                  borderRadius: 2,
                  overflow: 'auto',
                  maxHeight: 400,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'grey.100',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    textAlign: 'left',
                  }}
                >
                  {JSON.stringify(appSpec.spec, null, 2)}
                </Typography>
              </Box>

              <Button
                variant="outlined"
                color="error"
                onClick={handleReset}
                startIcon={<RestartAltIcon />}
                sx={{
                  mt: 2,
                  borderRadius: 28,
                  px: 3,
                  py: 1,
                }}
              >
                {t('voiceToApp.controls.tryAgain')}
              </Button>
            </Stack>
          </Paper>
        </Grow>
      )}

      <ProgressModal
        open={state.uploading}
        progress={state.progress}
        isComplete={state.uploadComplete}
        processingMessage={t('voiceToApp.messages.processing')}
        completeMessage={t('voiceToApp.messages.complete')}
        onReset={handleReset}
      />

      <FeedbackSnackbar
        open={!!state.error}
        message={state.error || t('voiceToApp.messages.error')}
        severity="error"
        onClose={() => setState((prev) => ({ ...prev, error: null }))}
      />
    </Box>
  );
};

export default VoiceToApp;
